import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TenantsService } from '../tenants.service';
import { AuthService } from '../../../../core/services/auth.service';
import { CustomValidators } from '../../../../shared/validators/custom-validators';
import { PhoneInputDirective } from '../../../../shared/directives/phone-input.directive';

@Component({
  selector: 'app-tenant-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDatepickerModule,
    MatNativeDateModule,
    PhoneInputDirective,
  ],
  templateUrl: './tenant-form.component.html',
  styleUrls: ['./tenant-form.component.scss'],
})
export class TenantFormComponent implements OnInit {
  tenantForm: FormGroup;
  loading = false;
  isEditMode = false;
  tenantId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private tenantsService: TenantsService,
    private snackBar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
  ) {
    this.tenantForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      business_type: ['', [Validators.maxLength(100)]],
      country: ['', [Validators.maxLength(100)]],
      phone: ['', [CustomValidators.phone()]],
      trial_ends_at: [''],
      is_active: [true],
    });
  }

  ngOnInit(): void {
    // Redirect Admin users to dashboard - they shouldn't access tenant management
    const currentUser = this.authService.getCurrentUserValue();
    if (currentUser?.role?.slug === 'admin') {
      this.snackBar.open(
        'Access denied: Tenant management is restricted to Super Admins',
        'Close',
        {
          duration: 3000,
        },
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    this.tenantId = this.route.snapshot.paramMap.get('id');
    if (this.tenantId) {
      this.isEditMode = true;
      this.loadTenant();
    }
  }

  loadTenant(): void {
    if (!this.tenantId) return;

    this.loading = true;
    this.tenantsService.getTenant(this.tenantId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const tenant = response.data;
          this.tenantForm.patchValue({
            name: tenant.name,
            business_type: tenant.business_type,
            country: tenant.country,
            phone: tenant.phone,
            trial_ends_at: tenant.trial_ends_at
              ? new Date(tenant.trial_ends_at)
              : null,
            is_active: tenant.is_active,
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading tenant:', error);
        this.snackBar.open('Failed to load tenant details', 'Close', {
          duration: 3000,
        });
        this.loading = false;
        this.router.navigate(['/admin/tenants']);
      },
    });
  }

  onSubmit(): void {
    if (this.tenantForm.invalid) {
      this.tenantForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    const formValue = this.tenantForm.value;

    // Format trial_ends_at date if provided
    if (formValue.trial_ends_at) {
      formValue.trial_ends_at = new Date(formValue.trial_ends_at).toISOString();
    }

    const request$ =
      this.isEditMode && this.tenantId
        ? this.tenantsService.updateTenant(this.tenantId, formValue)
        : this.tenantsService.createTenant(formValue);

    request$.subscribe({
      next: (response) => {
        if (response.success) {
          this.snackBar.open(
            `Tenant ${this.isEditMode ? 'updated' : 'created'} successfully`,
            'Close',
            { duration: 3000 },
          );
          this.router.navigate(['/admin/tenants']);
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error saving tenant:', error);
        const errorMessage =
          error.error?.message ||
          `Failed to ${this.isEditMode ? 'update' : 'create'} tenant`;
        this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        this.loading = false;
      },
    });
  }

  onCancel(): void {
    this.router.navigate(['/admin/tenants']);
  }

  getErrorMessage(fieldName: string): string {
    const control = this.tenantForm.get(fieldName);
    if (control?.hasError('required')) {
      return 'This field is required';
    }
    if (control?.hasError('phone')) {
      return 'Enter a valid 10-digit number starting with 0 (e.g. 0771234567)';
    }
    if (control?.hasError('maxlength')) {
      const maxLength = control.errors?.['maxlength'].requiredLength;
      return `Maximum length is ${maxLength} characters`;
    }
    return '';
  }
}
